import { useMemo } from 'react';

/**
 * S-08: audit_logs の before / after 差分表示 (依存ゼロの自前実装)。
 *
 * 設計判断:
 * - jsdiff / react-diff-viewer など外部 lib は導入しない (PR #98 review #1 で承認)
 * - JSON.stringify(値, null, 2) で正規化して line-by-line 比較
 * - 簡易 LCS で「追加 / 削除 / 共通」を判定し、行単位で背景色を変える
 *   - 追加（after にだけある）: success の面
 *   - 削除（before にだけある）: 濃い灰の面 + 危険色の文字（S-137 v2 / 生パレットを使わない）
 *   - 共通: 無色
 * - 大型 doc 対策はスクロール領域に押し込んで対応 (max-height)
 */
export interface AuditLogDiffProps {
  before: unknown;
  after: unknown;
}

interface DiffLine {
  kind: 'add' | 'remove' | 'same';
  text: string;
}

/** JSON 表現が無い (undefined / null) は空配列、それ以外は JSON.stringify を改行分割 */
export function stringifyForDiff(value: unknown): string[] {
  if (value === undefined || value === null) return ['(none)'];
  try {
    return JSON.stringify(value, null, 2).split('\n');
  } catch {
    return [String(value)];
  }
}

/**
 * Myers の LCS を簡易実装。audit_logs の before/after は通常 50 行未満なので
 * O(n*m) の DP テーブル方式で十分 (深刻な性能問題にならない)。
 */
export function computeLineDiff(
  beforeLines: readonly string[],
  afterLines: readonly string[],
): DiffLine[] {
  const n = beforeLines.length;
  const m = afterLines.length;
  // dp[i][j] = beforeLines[0..i] と afterLines[0..j] の LCS 長
  const dp: number[][] = Array.from({ length: n + 1 }, () =>
    new Array<number>(m + 1).fill(0),
  );
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      if (beforeLines[i - 1] === afterLines[j - 1]) {
        dp[i]![j] = dp[i - 1]![j - 1]! + 1;
      } else {
        dp[i]![j] = Math.max(dp[i - 1]![j]!, dp[i]![j - 1]!);
      }
    }
  }

  // backtrack して diff 生成
  const result: DiffLine[] = [];
  let i = n;
  let j = m;
  while (i > 0 && j > 0) {
    if (beforeLines[i - 1] === afterLines[j - 1]) {
      result.unshift({ kind: 'same', text: beforeLines[i - 1]! });
      i--;
      j--;
    } else if ((dp[i - 1]![j] ?? 0) >= (dp[i]![j - 1] ?? 0)) {
      result.unshift({ kind: 'remove', text: beforeLines[i - 1]! });
      i--;
    } else {
      result.unshift({ kind: 'add', text: afterLines[j - 1]! });
      j--;
    }
  }
  while (i > 0) {
    result.unshift({ kind: 'remove', text: beforeLines[i - 1]! });
    i--;
  }
  while (j > 0) {
    result.unshift({ kind: 'add', text: afterLines[j - 1]! });
    j--;
  }
  return result;
}

const LINE_CLASS: Record<DiffLine['kind'], string> = {
  add: 'block bg-success-tint text-success-ink',
  remove: 'block bg-surface-3 text-danger-ink',
  same: 'block text-ink',
};

const MARKER_CLASS: Record<DiffLine['kind'], string> = {
  add: 'inline-block w-6 font-medium text-success-ink',
  remove: 'inline-block w-6 font-medium text-danger-ink',
  same: 'inline-block w-6 text-ink-3',
};

export function AuditLogDiff({ before, after }: AuditLogDiffProps) {
  const lines = useMemo(() => {
    return computeLineDiff(stringifyForDiff(before), stringifyForDiff(after));
  }, [before, after]);

  return (
    <pre
      className="max-h-80 overflow-auto rounded-lg bg-surface-2 p-3 font-mono text-label leading-relaxed whitespace-pre"
      aria-label="before/after 差分"
    >
      {lines.map((line, idx) => (
        <div key={idx} className={LINE_CLASS[line.kind]}>
          <span className={MARKER_CLASS[line.kind]}>{markerFor(line.kind)}</span>
          <span>{line.text}</span>
        </div>
      ))}
    </pre>
  );
}

function markerFor(kind: DiffLine['kind']): string {
  switch (kind) {
    case 'add':
      return '+ ';
    case 'remove':
      return '- ';
    case 'same':
      return '  ';
  }
}
